import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchWithAuth } from '@/data/api/client'
import type {
    SavedViewResponse,
    SavedViewsListResponse,
    CreateSavedViewRequest,
    UpdateSavedViewRequest,
} from '@/data/types/views.types'

// API functions
async function listViews(): Promise<SavedViewsListResponse> {
    return fetchWithAuth('/api/v1/views/')
}

async function getView(viewId: string): Promise<SavedViewResponse> {
    return fetchWithAuth(`/api/v1/views/${viewId}`)
}

async function createView(data: CreateSavedViewRequest): Promise<SavedViewResponse> {
    return fetchWithAuth('/api/v1/views/', {
        method: 'POST',
        body: JSON.stringify(data),
    })
}

async function updateView(viewId: string, data: UpdateSavedViewRequest): Promise<SavedViewResponse> {
    return fetchWithAuth(`/api/v1/views/${viewId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    })
}

async function deleteView(viewId: string): Promise<void> {
    return fetchWithAuth(`/api/v1/views/${viewId}`, {
        method: 'DELETE',
    })
}

// React Query hooks

/**
 * Fetch all saved views for the organization
 */
export function useSavedViewsList() {
    return useQuery({
        queryKey: ['views'],
        queryFn: listViews,
        staleTime: 60 * 1000, // Cache for 1 minute
    })
}

/**
 * Fetch a single saved view by ID
 */
export function useSavedView(viewId: string | null) {
    return useQuery({
        queryKey: ['views', viewId],
        queryFn: () => getView(viewId!),
        enabled: !!viewId,
        staleTime: 60 * 1000,
    })
}

/**
 * Create a new saved view
 */
export function useCreateSavedView() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: createView,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['views'] })
        },
    })
}

/**
 * Update an existing saved view
 */
export function useUpdateSavedView() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ viewId, data }: { viewId: string; data: UpdateSavedViewRequest }) =>
            updateView(viewId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['views'] })
            queryClient.invalidateQueries({ queryKey: ['views', variables.viewId] })
        },
    })
}

/**
 * Delete a saved view
 */
export function useDeleteSavedView() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: deleteView,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['views'] })
        },
    })
}

